// src/hooks/insights/useEarliestDataDate.ts
//
// Lazily discovers the org's earliest `DailyOrgRollup.event_date`, to bound the Custom range
// picker's lower date (Part B). Fixed presets never pay this cost - the fetch only starts
// once `ensureLoaded()` is called, which `Insights.tsx` does the first time the user selects
// Custom. The result is cached per `orgCode` in a plain ref (not global/persisted storage,
// so it lives only as long as the page does) - switching back and forth between Custom and a
// fixed preset never re-issues the query.
//
// A thrown error and a legitimate `null` result (a brand-new org with zero rows) are folded
// into the same "unknown" outcome here, because callers treat them identically: both mean
// "fall back to the default floor and a neutral helper message," never a specific (and
// possibly wrong) start date.

import { useCallback, useRef, useState } from 'react';
import { getEarliestDataDate } from '@/repository/InsightsDAO';

export type EarliestDataDateState =
  | { status: 'idle'; earliestDate: null }
  | { status: 'loading'; earliestDate: null }
  | { status: 'ready'; earliestDate: string | null };

const INITIAL_STATE: EarliestDataDateState = { status: 'idle', earliestDate: null };

async function loadEarliestDataDateSafely(orgCode: string): Promise<string | null> {
  try {
    return await getEarliestDataDate(orgCode);
  } catch (error) {
    // Discovery failing must never block the picker (Part B) - the caller can't (and
    // doesn't need to) tell this apart from a legitimate zero-rows null.
    console.error(
      'useEarliestDataDate: getEarliestDataDate failed, falling back to the default floor',
      error,
    );
    return null;
  }
}

export type UseEarliestDataDateResult = {
  state: EarliestDataDateState;
  ensureLoaded: () => void;
};

/**
 * Returns the discovery state plus a trigger that starts the lazy, cached fetch. Safe to call
 * `ensureLoaded` on every render while Custom is selected - it is a no-op once a fetch has
 * started or finished for the current `orgCode`.
 */
export function useEarliestDataDate(orgCode: string | null): UseEarliestDataDateResult {
  const [state, setState] = useState<EarliestDataDateState>(INITIAL_STATE);
  const cache = useRef<Map<string, string | null>>(new Map());
  const fetchStartedForOrgCode = useRef<string | null>(null);

  const ensureLoaded = useCallback(() => {
    if (!orgCode) return;

    const cached = cache.current.get(orgCode);
    if (cached !== undefined) {
      setState({ status: 'ready', earliestDate: cached });
      return;
    }
    if (fetchStartedForOrgCode.current === orgCode) return;

    fetchStartedForOrgCode.current = orgCode;
    setState({ status: 'loading', earliestDate: null });

    loadEarliestDataDateSafely(orgCode).then((earliestDate) => {
      cache.current.set(orgCode, earliestDate);
      setState({ status: 'ready', earliestDate });
    });
  }, [orgCode]);

  return { state, ensureLoaded };
}
